import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Panel, Pill, Loading, ErrorNote, Empty, formatDate, formatDateTime } from '../components/ui';
import { useAuth, useRight } from '../lib/auth';
import { ROLE_LABELS, ROLE_RIGHTS, ROLES, type Role } from '@inbrief/shared';

interface TeamData {
  users: {
    id: string; name: string; email: string; role: Role;
    last_seen_at: string | null; disabled_at: string | null; practice_name: string | null;
  }[];
  practices: { id: string; name: string; ods_code: string | null; list_size: number | null; user_count: string }[];
  organisation: { name: string; kind: string; icb: string | null; plan_id: string; founding_customer: boolean };
}

interface RetentionData {
  retentionYears: number;
  usingDefault: boolean;
  defaultYears: number;
  mode: 'report' | 'delete';
  expired: { title: string; uploadedAt: string }[];
  sweeps: {
    mode: string; documents_expired: number; documents_deleted: number;
    files_deleted: number; created_at: string; error: string | null;
  }[];
  note: string;
}

export function Admin() {
  const { user } = useAuth();
  const canManage = useRight('settings');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<TeamData>('/api/team'),
  });

  const audit = useQuery({
    queryKey: ['audit'],
    queryFn: () => api.get<{ entries: any[] }>('/api/audit?limit=40'),
    enabled: canManage,
  });

  const retention = useQuery({
    queryKey: ['retention'],
    queryFn: () => api.get<RetentionData>('/api/retention'),
    enabled: canManage,
  });

  const setRetention = useMutation({
    mutationFn: (retentionYears: number | null) =>
      api.patch('/api/retention', { retentionYears }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['retention'] }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['team'] });

  const updateUser = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api.patch(`/api/team/users/${id}`, body),
    onSuccess: invalidate,
  });
  const addPractice = useMutation({
    mutationFn: (name: string) => api.post('/api/team/practices', { name }),
    onSuccess: invalidate,
  });
  const addUser = useMutation({
    mutationFn: (body: { name: string; email: string; role: Role; password: string }) =>
      api.post('/api/team/users', body),
    onSuccess: invalidate,
  });

  if (isLoading) return <Loading what="Loading your team" />;
  if (error) return <ErrorNote error={error} />;

  return (
    <>
      <PageHeader
        title="Team & Practices"
        description={`${data?.organisation.name} — who can do what, and which practices are covered.`}
      />

      <Panel title="What each role can do" icon="⚿">
        <div className="table-wrap">
          <table className="dt">
            <caption className="visually-hidden">Role permissions</caption>
            <thead>
              <tr>
                <th scope="col">Role</th>
                <th scope="col">Contracts</th>
                <th scope="col">Communications</th>
                <th scope="col">Translation</th>
                <th scope="col">Settings</th>
              </tr>
            </thead>
            <tbody>
              {ROLES.map((role) => (
                <tr key={role}>
                  <th scope="row">{ROLE_LABELS[role]}</th>
                  {(['extraction', 'comms', 'translation', 'settings'] as const).map((right) => (
                    <td key={right}>{ROLE_RIGHTS[role][right] ? 'Yes' : '—'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="field-hint">
          Enforced on the server for every request, not just hidden in this interface.
        </p>
      </Panel>

      <Panel
        title="People"
        icon="◉"
        actions={
          <span className="row-gap">
            <span className="field-hint">{data?.users.length} accounts</span>
            {canManage ? (
              <button
                className="btn btn-secondary btn-small"
                onClick={() => {
                  const name = window.prompt('Full name');
                  if (!name?.trim()) return;
                  const email = window.prompt('Email address');
                  if (!email?.trim()) return;
                  const role = window.prompt(
                    `Role — one of:\n${ROLES.join('\n')}`,
                    'practice-manager',
                  );
                  if (!role || !(ROLES as readonly string[]).includes(role)) {
                    if (role !== null) window.alert('That is not a valid role.');
                    return;
                  }
                  // Set here rather than emailed: there is no mail transport in
                  // this build, and inventing one to send credentials would be
                  // the wrong first thing to add.
                  const password = window.prompt('Initial password (at least 12 characters)');
                  if (!password || password.length < 12) {
                    if (password !== null) window.alert('That password is too short.');
                    return;
                  }
                  addUser.mutate({ name: name.trim(), email: email.trim(), role: role as Role, password });
                }}
              >
                Add someone
              </button>
            ) : null}
          </span>
        }
      >
        <div className="table-wrap">
          <table className="dt">
            <caption className="visually-hidden">Team members</caption>
            <thead>
              <tr>
                <th scope="col">Name</th><th scope="col">Practice</th>
                <th scope="col">Role</th><th scope="col">Last seen</th><th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {data?.users.map((u) => (
                <tr key={u.id}>
                  <th scope="row">
                    {u.name}
                    <div className="field-hint">{u.email}</div>
                  </th>
                  <td>{u.practice_name ?? '—'}</td>
                  <td>
                    {canManage && u.id !== user?.userId ? (
                      <select
                        value={u.role}
                        aria-label={`Role for ${u.name}`}
                        onChange={(e) => updateUser.mutate({ id: u.id, body: { role: e.target.value } })}
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </select>
                    ) : (
                      ROLE_LABELS[u.role]
                    )}
                  </td>
                  <td>{formatDateTime(u.last_seen_at)}</td>
                  <td>
                    {u.disabled_at
                      ? <Pill tone="rejected">Disabled</Pill>
                      : <Pill tone="approved">Active</Pill>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ErrorNote error={updateUser.error ?? addUser.error} />
      </Panel>

      <Panel
        title="Practices"
        icon="⌂"
        actions={canManage ? (
          <button
            className="btn btn-secondary btn-small"
            onClick={() => {
              const name = window.prompt('Practice name');
              if (name?.trim()) addPractice.mutate(name.trim());
            }}
          >
            Add a practice
          </button>
        ) : null}
      >
        <div className="table-wrap">
          <table className="dt">
            <caption className="visually-hidden">Practices</caption>
            <thead>
              <tr>
                <th scope="col">Practice</th><th scope="col">ODS code</th>
                <th scope="col">List size</th><th scope="col">People</th>
              </tr>
            </thead>
            <tbody>
              {data?.practices.map((p) => (
                <tr key={p.id}>
                  <th scope="row">{p.name}</th>
                  <td>{p.ods_code ?? '—'}</td>
                  <td>{p.list_size?.toLocaleString('en-GB') ?? '—'}</td>
                  <td>{p.user_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ErrorNote error={addPractice.error} />
      </Panel>

      {canManage && retention.data ? (
        <Panel
          title="Retention"
          icon="◷"
          actions={
            <span className="row-gap">
              <Pill tone={retention.data.mode === 'delete' ? 'rejected' : 'info'}>
                {retention.data.mode === 'delete' ? 'Deletes automatically' : 'Reports only'}
              </Pill>
              <button
                className="btn btn-secondary btn-small"
                onClick={() => {
                  const answer = window.prompt(
                    'Keep uploaded documents for how many years? Leave blank to use the deployment default.',
                    String(retention.data!.retentionYears),
                  );
                  if (answer === null) return;
                  const trimmed = answer.trim();
                  setRetention.mutate(trimmed === '' ? null : Number(trimmed));
                }}
              >
                Change period
              </button>
            </span>
          }
        >
          <div className="kv">
            <span className="fact-label">Documents kept for</span>
            <span className="fact-value">
              {retention.data.retentionYears} years
              {retention.data.usingDefault ? ' (deployment default)' : ''}
            </span>
          </div>
          <p className="field-hint">{retention.data.note}</p>

          {retention.data.expired.length ? (
            <div className={retention.data.mode === 'delete' ? 'warn-box' : 'note-box'}>
              <strong>
                {retention.data.expired.length} document
                {retention.data.expired.length === 1 ? '' : 's'} past the retention period.
              </strong>
              <ul>
                {retention.data.expired.slice(0, 10).map((e, i) => (
                  <li key={i}>{e.title} — uploaded {formatDate(e.uploadedAt)}</li>
                ))}
              </ul>
              {retention.data.mode === 'report'
                ? 'Nothing is deleted automatically on this deployment. Delete them from the Documents page when you are ready.'
                : 'These will be deleted, with their files, at the next sweep.'}
            </div>
          ) : (
            <p className="field-hint">Nothing is past its retention period.</p>
          )}

          <ErrorNote error={setRetention.error} />

          {retention.data.sweeps.length ? (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="dt">
                <caption className="visually-hidden">Recent retention sweeps</caption>
                <thead>
                  <tr>
                    <th scope="col">When</th><th scope="col">Mode</th>
                    <th scope="col">Past retention</th><th scope="col">Deleted</th>
                  </tr>
                </thead>
                <tbody>
                  {retention.data.sweeps.map((s, i) => (
                    <tr key={i}>
                      <td>{formatDateTime(s.created_at)}</td>
                      <td>{s.mode}</td>
                      <td>{s.documents_expired}</td>
                      <td>{s.documents_deleted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </Panel>
      ) : null}

      {canManage ? (
        <Panel title="Audit log" icon="≡">
          <p className="field-hint">
            Every access, extraction, review, approval and export. Append-only and retained —
            NHS buyers ask for this during assessment, and again during an incident.
          </p>
          {audit.data?.entries.length === 0 ? (
            <Empty>Nothing recorded yet.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="dt">
                <caption className="visually-hidden">Audit entries</caption>
                <thead>
                  <tr>
                    <th scope="col">When</th><th scope="col">Action</th>
                    <th scope="col">Who</th><th scope="col">Subject</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.data?.entries.map((e) => (
                    <tr key={e.id}>
                      <td>{formatDateTime(e.created_at)}</td>
                      <td>{e.action}</td>
                      <td>{e.user_email ?? 'System'}</td>
                      <td>{e.subject_type ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      ) : null}
    </>
  );
}
